import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { User, AuthState } from '../types/auth';
import { db } from '../services/backend';
import { auth } from '../lib/firebase';

interface AuthContextType extends AuthState {
  login: (email: string, password: string, remember: boolean) => Promise<void>;
  logout: () => void;
  updateCurrentUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    const syncSession = async () => {
      const storedUser = localStorage.getItem('Veraz_user');

      if (auth.currentUser) {
        const token = await auth.currentUser.getIdToken();
        const fallbackUser: User = {
          id: auth.currentUser.uid,
          email: auth.currentUser.email || '',
          name: auth.currentUser.displayName || 'Admin',
          role: 'admin',
          avatar: auth.currentUser.photoURL || undefined,
        };

        setState({
          token,
          user: storedUser ? JSON.parse(storedUser) : fallbackUser,
          isAuthenticated: true,
          isLoading: false,
        });
        return;
      }

      localStorage.removeItem('Veraz_token');
      localStorage.removeItem('Veraz_user');
      setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
    };

    const unsubscribe = onAuthStateChanged(auth, () => {
      syncSession().catch((error) => {
        console.error('Failed to sync admin session with Firebase Auth', error);
        localStorage.removeItem('Veraz_token');
        localStorage.removeItem('Veraz_user');
        setState({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
      });
    });

    return unsubscribe;
  }, []);

  const login = async (email: string, password: string, remember: boolean) => {
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      const response = await db.login(email, password);
      if (response) {
        const { user, token } = response;

        if (remember) {
          localStorage.setItem('Veraz_token', token);
          localStorage.setItem('Veraz_user', JSON.stringify(user));
        } else {
          localStorage.setItem('Veraz_token', token);
          localStorage.setItem('Veraz_user', JSON.stringify(user));
        }

        setState({
          user: user as User,
          token,
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        throw new Error('Credenciais invÃ¡lidas');
      }
    } catch (error) {
      setState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const logout = () => {
    firebaseSignOut(auth).catch((error) => {
      console.error('Firebase logout error', error);
    });
    localStorage.removeItem('Veraz_token');
    localStorage.removeItem('Veraz_user');
    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
  };

  const updateCurrentUser = (updates: Partial<User>) => {
    setState((prev) => {
      if (!prev.user) return prev;

      const nextUser = { ...prev.user, ...updates };
      localStorage.setItem('Veraz_user', JSON.stringify(nextUser));

      return {
        ...prev,
        user: nextUser,
      };
    });
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout, updateCurrentUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

